<template>
	<div class="price-comparison__wrapper">
		<p class="price-comparison__additional-filter__heading">
			<FontAwesomeIcon :icon="faFilter" />
			<span v-if="!isInModal" class="hidden-xs filter-text">{{ $t('WEBAPP_FILTERS') }}</span>
		</p>
		<div class="price-comparison__additional-filter">
			<ChipButtons>
				<ChipButton v-for="{ value, label } of filterOptions" :active="buyboxFilter === value" :key="value">
					<button @click.prevent="handleFilterClick({ presentationType: value, additionalContexts })">
						{{ label }}
					</button>
				</ChipButton>
			</ChipButtons>
		</div>

		<div class="country-switcher__wrapper">
			<p v-if="!isInModal" class="country-switcher__streaming_in">
				{{ $t('WEBAPP_COUNTRY_SWITCHER_STREAMING_IN') + ' ' }}
			</p>

			<button v-if="!isInModal" class="country-switcher__button" @click.prevent="openCountrySwitcherModal">
				{{ languageStore.countryEmoji }}

				<span v-if="!isInModal" class="country-switcher__country_name">
					{{ $t(`WEBAPP_COUNTRY_${languageStore.webLocale.toUpperCase()}`) }}
				</span>

				<FontAwesomeIcon :icon="faAngleDown" />
			</button>
		</div>
	</div>
</template>

<script setup lang="ts">
import CountrySwitcherModal from '@/features/buybox/components/CountrySwitcherModal.vue';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { BuyboxPresentationType, useUserStore } from '@/stores/pinia/useUserStore';
import type { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { ModalHelper } from '@/helpers/vfm/modal-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import { PresentationType, MonetizationType } from '@/@types/graphql-types';
import { PresentationTypes } from '@/interfaces/titles';
import ChipButton from '@/ui-components/ChipButton.vue';
import ChipButtons from '@/ui-components/ChipButtons.vue';
import { computed, onMounted, ref } from 'vue';
import { faAngleDown, faFilter } from '@fortawesome/free-solid-svg-icons';

const props = withDefaults(
	defineProps<{
		isInModal?: boolean;
		defaultPresentationType?: BuyboxPresentationType | null;
		additionalContexts?: SnowplowContext[];
		isEpisodeBuybox?: boolean;
		episodeBuyBoxPresentationType?: BuyboxPresentationType | null;
	}>(),
	{
		defaultPresentationType: null,
		additionalContexts: () => [],
		episodeBuyBoxPresentationType: null,
	}
);
const emits = defineEmits<{
	(e: 'episodeFilterChange', presentationType: BuyboxPresentationType | null): void;
}>();

const languageStore = useLanguageStore();
const userStore = useUserStore();

const route = useRoute();

const { t } = useI18n();
const filterOptions = computed(() => [
	{
		label: t('WEBAPP_QUALITY_BESTPRICE'),
		value: null,
		isQuality: false,
	},
	{
		label: t('WEBAPP_BUYBOX_FREE_FILTER'),
		value: MonetizationType.Free,
		isQuality: false,
	} as const,
	...PresentationTypes.filter(g => g.isQuality),
]);

function openCountrySwitcherModal() {
	ModalHelper.openModal(CountrySwitcherModal, { closable: true });
	TrackingHelper.trackEvent('userinteraction', { action: 'country_selector_clicked' }, props.additionalContexts);
}

async function handleFilterClick(payload: {
	presentationType: PresentationType | MonetizationType.Free | null;
	additionalContexts?: SnowplowContext[];
}) {
	// updating the buybox is expensive.
	// that's why we split it up in 1) optimistic UI update
	if (!props.isEpisodeBuybox) {
		userStore.setBuyboxFilterTypeOptimistic(payload.presentationType);
	} else {
		emits('episodeFilterChange', payload.presentationType);
	}
	// then 2) give the JS thread time to render the optimistic UI update immediately
	await new Promise(resolve => setTimeout(resolve, 0));
	// and 3) defer the expensive store commit to the end
	if (!props.isEpisodeBuybox) {
		userStore.setBuyboxPresentationType(payload);
	}
}

const _buyboxFilter = ref<undefined | null | PresentationType>(undefined);
const buyboxFilter = computed(() => {
	if (props.isEpisodeBuybox) {
		return props.episodeBuyBoxPresentationType;
	}
	// we need an additional state because buyboxFilterTypeOptimistic's `null` can mean multiple things:
	// - user hasn't filtered anything
	// - user has filtered on "BEST"
	if (_buyboxFilter.value !== undefined) {
		return _buyboxFilter.value;
	}
	return userStore.buyboxFilterTypeOptimistic !== null
		? userStore.buyboxFilterTypeOptimistic
		: userStore.buyboxPresentationType;
});

onMounted(() => {
	// for Apple TV+ search ads
	if (route.query.monetization_types === 'free') {
		return userStore.setBuyboxPresentationType({ presentationType: MonetizationType.Free });
	}

	if (!props.defaultPresentationType) return;

	userStore.setBuyboxPresentationType({
		presentationType: props.defaultPresentationType,
		additionalContexts: props.additionalContexts,
	});
});
</script>

<style lang="scss" scoped>
@import '~@/styles/variables.scss';

.price-comparison__wrapper {
	display: flex;
	align-items: center;
	padding-left: 10px;
	padding-right: 10px;
	background: $jw-color-darkblue--light;
}

.price-comparison__additional-filter {
	display: flex;
	align-items: center;
	justify-content: flex-start;
	padding: 4px 8px;
	background: $jw-color-darkblue--light;

	&__heading {
		color: var(--jw-color-secondary-tint);
		text-transform: uppercase;
		font-weight: bold;
		font-size: 14px;

		.filter-text {
			padding-left: 4px;
		}
	}
}

.country-switcher {
	&__wrapper {
		display: flex;
		align-items: center;
		justify-content: flex-start;
		margin-inline-start: auto;

		@media (max-width: 500px) {
			display: none;
		}
	}

	&__button {
		border: 1px solid transparent;
		border-radius: $jw-border-radius;
		font-size: 15px;
		background-color: var(--jw-color-secondary-shade);
		color: $jw-color-white;
		padding: 5px 10px;
		cursor: pointer;
		text-overflow: ellipsis;
		overflow: hidden;
		transition: background-color 0.1s linear;

		&:active {
			background-color: transparent;
		}
	}
}

.country-switcher__streaming_in {
	color: var(--jw-color-secondary-tint);
	background: $jw-color-darkblue--light;
	font-weight: bold;
	font-size: 15px;
	margin-inline-end: 8px;
}

@media (max-width: 1200px) {
	.country-switcher__country_name {
		display: none;
	}
}

@media (max-width: 1024px) {
	.country-switcher__streaming_in {
		display: none;
	}
}
</style>
